import { getTokenFromLS, useUserContext } from "@dexteel/mesf-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type {
	DocumentCatalogItem,
	PartInventoryItem,
} from "../models/Catalogs";
import {
	getDocumentsCatalog,
	getPartsInventoryCatalog,
} from "../repositories/CatalogsRepository";

export const CATALOG_QUERY_KEYS = {
	all: ["catalogs"] as const,
	partsInventory: () => ["catalogs", "partsInventory"] as const,
	documents: () => ["catalogs", "documents"] as const,
};

// Both catalogs are large and change rarely. Refreshed in the background on this interval and
// never dropped from memory while the application is open: the whole point is that a picker never
// waits for them again. mesf-core's QueryClient defaults cacheTime to one minute, which would undo
// the preloading, hence the explicit value here.
const CATALOG_STALE_TIME = 600 * 1000; // 10 minutes
const CATALOG_CACHE_TIME = Number.POSITIVE_INFINITY;

/**
 * Whether there is a session, so nothing is fetched from the login screen: the prefetch lives in
 * the navbar, which renders there too, and without this the two catalogs (16 MB between them) were
 * pulled with an expired token.
 * Both signals are needed. `userLogged` only turns true when someone actually logs in, so on a
 * reload with a valid session it stays false and the token is the one telling the truth; right
 * after logging in the token is already stored, and `userLogged` is what re-renders this hook.
 */
const useHasSession = () => {
	const {
		state: { userLogged },
	} = useUserContext();

	return !!userLogged || !!getTokenFromLS();
};

export const usePartsInventory = () => {
	const hasSession = useHasSession();

	const query = useQuery<PartInventoryItem[], Error>({
		queryKey: CATALOG_QUERY_KEYS.partsInventory(),
		// Wrapped so React Query does not hand its own context in as the options argument.
		queryFn: () => getPartsInventoryCatalog(),
		enabled: hasSession,
		staleTime: CATALOG_STALE_TIME,
		cacheTime: CATALOG_CACHE_TIME,
		refetchInterval: CATALOG_STALE_TIME, // Refreshed in the background, so a picker never waits for it.
	});

	return {
		parts: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error,
		refetch: query.refetch,
	};
};

export const useDocumentsCatalog = () => {
	const hasSession = useHasSession();

	const query = useQuery<DocumentCatalogItem[], Error>({
		queryKey: CATALOG_QUERY_KEYS.documents(),
		// Wrapped so React Query does not hand its own context in as the options argument.
		queryFn: () => getDocumentsCatalog(),
		enabled: hasSession,
		staleTime: CATALOG_STALE_TIME,
		cacheTime: CATALOG_CACHE_TIME,
		refetchInterval: CATALOG_STALE_TIME, // Refreshed in the background, so a picker never waits for it.
	});

	return {
		documents: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error,
		refetch: query.refetch,
	};
};

/**
 * For the pickers that load their list imperatively instead of through a hook.
 *
 * Whatever is already in memory is returned straight away, even when it is past its window, and
 * the refresh happens behind the picker. Waiting for a fresh copy would put the spinner back on a
 * screen that already had the data, which is the whole thing this feature exists to remove; the
 * background refresh and the reload button cover the freshness side.
 */
export const useCatalogFetchers = () => {
	const queryClient = useQueryClient();

	// Stable across renders so callers can list them as effect dependencies without looping.
	const fetchPartsInventory = useCallback(async () => {
		const options = {
			queryKey: CATALOG_QUERY_KEYS.partsInventory(),
			// Wrapped so React Query does not hand its own context in as the options argument.
			queryFn: () => getPartsInventoryCatalog(),
			staleTime: CATALOG_STALE_TIME,
			cacheTime: CATALOG_CACHE_TIME,
		};

		const cached = queryClient.getQueryData<PartInventoryItem[]>(
			CATALOG_QUERY_KEYS.partsInventory(),
		);
		if (cached) {
			// Refreshes only if the copy is past its window, and never blocks the caller.
			queryClient.fetchQuery<PartInventoryItem[]>(options).catch(() => {});
			return cached;
		}

		return queryClient.fetchQuery<PartInventoryItem[]>(options);
	}, [queryClient]);

	const fetchDocumentsCatalog = useCallback(async () => {
		const options = {
			queryKey: CATALOG_QUERY_KEYS.documents(),
			// Wrapped so React Query does not hand its own context in as the options argument.
			queryFn: () => getDocumentsCatalog(),
			staleTime: CATALOG_STALE_TIME,
			cacheTime: CATALOG_CACHE_TIME,
		};

		const cached = queryClient.getQueryData<DocumentCatalogItem[]>(
			CATALOG_QUERY_KEYS.documents(),
		);
		if (cached) {
			queryClient.fetchQuery<DocumentCatalogItem[]>(options).catch(() => {});
			return cached;
		}

		return queryClient.fetchQuery<DocumentCatalogItem[]>(options);
	}, [queryClient]);

	return { fetchPartsInventory, fetchDocumentsCatalog };
};

/**
 * For the reload button at the bottom of a grid. Goes past both copies in one call: the API is told
 * to ignore what it has stored and refresh it from the database, and the answer replaces what the
 * browser was holding.
 */
export const useCatalogReload = () => {
	const queryClient = useQueryClient();

	const reloadPartsInventory = useCallback(async () => {
		const parts = await getPartsInventoryCatalog({ invalidateCache: true });
		queryClient.setQueryData(CATALOG_QUERY_KEYS.partsInventory(), parts);
		return parts;
	}, [queryClient]);

	const reloadDocumentsCatalog = useCallback(async () => {
		const documents = await getDocumentsCatalog({ invalidateCache: true });
		queryClient.setQueryData(CATALOG_QUERY_KEYS.documents(), documents);
		return documents;
	}, [queryClient]);

	return { reloadPartsInventory, reloadDocumentsCatalog };
};

/**
 * When the browser last received each catalog, for the "updated at" label. Subscribes to the cache
 * (rather than reading it once) so the label follows a reload on its own; `enabled: false` keeps it
 * from triggering a request of its own.
 */
export const useCatalogUpdatedAt = () => {
	const parts = useQuery<PartInventoryItem[], Error>({
		queryKey: CATALOG_QUERY_KEYS.partsInventory(),
		queryFn: () => getPartsInventoryCatalog(),
		enabled: false,
		cacheTime: CATALOG_CACHE_TIME,
	});

	const documents = useQuery<DocumentCatalogItem[], Error>({
		queryKey: CATALOG_QUERY_KEYS.documents(),
		queryFn: () => getDocumentsCatalog(),
		enabled: false,
		cacheTime: CATALOG_CACHE_TIME,
	});

	return {
		partsUpdatedAt: parts.dataUpdatedAt || undefined,
		documentsUpdatedAt: documents.dataUpdatedAt || undefined,
	};
};
